/**
 * Verificação de eficácia. Ação concluída não encerra o problema.
 * O histórico acumula tentativas. Nada aqui grava no banco.
 */
import type { IssueMetadata, IssueStatus } from '../types/issue';
import { appendToHistory, createHistoryEntry } from './issueHistory';
import { readOperationalOrigin, type OperationalOrigin } from '../components/ops/exceptionLoop';

export type EfficacyResult = 'effective' | 'ineffective';

export interface CountermeasureRef {
  id: string;
  status: string;
}

export interface EfficacyAttempt {
  result: EfficacyResult;
  note: string;
  at: string;
  actor_id?: string | null;
  actor_name?: string | null;
  action_ids: string[];
}

const CLOSED_ACTION = new Set(['completed', 'cancelled']);

export function openCountermeasures(actions: CountermeasureRef[]): CountermeasureRef[] {
  return actions.filter((action) => !CLOSED_ACTION.has(action.status));
}

export function readyForEfficacyCheck(
  actions: CountermeasureRef[],
  issueStatus: IssueStatus | string,
): boolean {
  if (issueStatus === 'closed') return false;
  if (openCountermeasures(actions).length > 0) return false;
  return actions.some((action) => action.status === 'completed');
}

export function problemResolvedByActionCompletion(): boolean {
  return false;
}

export function efficacyAttempts(metadata: IssueMetadata | null | undefined): EfficacyAttempt[] {
  const history = metadata?.history ?? [];
  return history
    .map((entry) => entry.meta?.efficacy as EfficacyAttempt | undefined)
    .filter((attempt): attempt is EfficacyAttempt => Boolean(attempt?.result && attempt.at))
    .sort((left, right) => new Date(left.at).getTime() - new Date(right.at).getTime());
}

export function recordEfficacyAttempt(
  metadata: IssueMetadata | null | undefined,
  attempt: EfficacyAttempt,
): IssueMetadata {
  const message = attempt.result === 'effective'
    ? 'Eficácia confirmada'
    : 'Verificação: não foi eficaz';
  return appendToHistory(
    metadata,
    createHistoryEntry('efficacy_check', message, {
      actor_id: attempt.actor_id ?? undefined,
      actor_name: attempt.actor_name ?? undefined,
      meta: { efficacy: attempt },
    }),
  );
}

export function statusAfterEffectiveConfirmation(): IssueStatus {
  return 'closed';
}

export interface CloseSubjectAction extends CountermeasureRef {
  completed_at?: string | null;
}

export type CloseBlock =
  | { allowed: true }
  | { allowed: false; reason: string };

const MANAGERIAL_ROLES = new Set(['lead', 'manager', 'director']);

function isRelevantCountermeasure(action: CloseSubjectAction): boolean {
  return action.status === 'completed';
}

function attemptCovers(attempt: EfficacyAttempt, actions: CloseSubjectAction[]): boolean {
  if (attempt.result !== 'effective') return false;
  return actions.every((action) => coversAction(attempt, action));
}

function coversAction(attempt: EfficacyAttempt, action: CloseSubjectAction): boolean {
  if (attempt.result !== 'effective') return false;
  if (!attempt.action_ids.includes(action.id)) return false;
  if (!action.completed_at) return true;
  return new Date(attempt.at).getTime() >= new Date(action.completed_at).getTime();
}

export type ClosureCase = 'A' | 'B' | 'C';

export interface ClosureEvaluation {
  canClose: boolean;
  case: ClosureCase;
  blockers: string[];
  completedActionIds: string[];
  coveredActionIds: string[];
  requiresEfficacy: boolean;
  requiresClosureReason: boolean;
  reason: string | null;
}

/** Confirmar eficácia registra a tentativa. Não encerra o problema. */
export const EFFICACY_CONFIRMATION_CLOSES_ISSUE = false;

/** validation_config.result é critério histórico. Não autoriza fechamento. */
export function validationResultAuthorizesClosure(): boolean {
  return false;
}

/** Oportunidade não bloqueia e não autoriza o encerramento. */
export const OPPORTUNITY_CLOSURE_EFFECT = { blocks: false, authorizes: false } as const;

export function directCountermeasures<T extends { problem_id?: string | null }>(
  actions: T[],
  issueId: string,
): T[] {
  return actions.filter((action) => action.problem_id === issueId);
}

export function latestReopenAt(metadata: IssueMetadata | null | undefined): string | null {
  const stamps = (metadata?.history ?? [])
    .filter((entry) => entry.type === 'reopened' && entry.at)
    .map((entry) => entry.at);
  if (stamps.length === 0) return null;
  return stamps.reduce((latest, stamp) => (
    new Date(stamp).getTime() > new Date(latest).getTime() ? stamp : latest
  ));
}

export function authorizingAttempts(
  attempts: EfficacyAttempt[],
  reopenAt?: string | null,
): EfficacyAttempt[] {
  if (!reopenAt) return attempts;
  const cutoff = new Date(reopenAt).getTime();
  return attempts.filter((attempt) => new Date(attempt.at).getTime() > cutoff);
}

function activeBlockReason(count: number): string {
  if (count === 1) return 'Existe 1 contramedida ainda em andamento.';
  return `Há ${count} contramedidas ainda em andamento.`;
}

function uncoveredReason(completed: CloseSubjectAction[], attempts: EfficacyAttempt[]): string {
  const best = attempts.reduce((max, attempt) => {
    if (attempt.result !== 'effective') return max;
    const count = completed.filter((action) => coversAction(attempt, action)).length;
    return Math.max(max, count);
  }, 0);
  if (best > 0 && best < completed.length) {
    return 'A verificação de eficácia não cobre todas as contramedidas concluídas.';
  }
  if (completed.length === 1) return '1 contramedida concluída ainda não possui eficácia confirmada.';
  return `${completed.length} contramedidas concluídas ainda não possuem eficácia confirmada.`;
}

/**
 * Regra única de encerramento.
 * A: contramedida direta ativa bloqueia.
 * B: cada concluída precisa de uma mesma tentativa effective posterior à conclusão e à última reabertura.
 * C: sem concluída, exige motivo. Cancelada não exige eficácia. Oportunidade não entra.
 */
export function evaluateIssueClosure(input: {
  actions: CloseSubjectAction[];
  attempts: EfficacyAttempt[];
  closureReason?: string | null;
  reopenAt?: string | null;
  metadata?: IssueMetadata | null;
}): ClosureEvaluation {
  const reopenAt = input.reopenAt === undefined ? latestReopenAt(input.metadata) : input.reopenAt;
  const valid = authorizingAttempts(input.attempts, reopenAt);
  const active = openCountermeasures(input.actions);
  const completed = input.actions.filter(isRelevantCountermeasure);
  const completedActionIds = completed.map((action) => action.id);
  const coveredActionIds = completed
    .filter((action) => valid.some((attempt) => coversAction(attempt, action)))
    .map((action) => action.id);

  if (active.length > 0) {
    return {
      canClose: false,
      case: 'A',
      blockers: ['active_countermeasures'],
      completedActionIds,
      coveredActionIds,
      requiresEfficacy: completed.length > 0,
      requiresClosureReason: false,
      reason: activeBlockReason(active.length),
    };
  }

  if (completed.length > 0) {
    const covered = valid.some((attempt) => attemptCovers(attempt, completed));
    if (!covered) {
      return {
        canClose: false,
        case: 'B',
        blockers: ['efficacy_missing'],
        completedActionIds,
        coveredActionIds,
        requiresEfficacy: true,
        requiresClosureReason: false,
        reason: uncoveredReason(completed, valid),
      };
    }
    return {
      canClose: true,
      case: 'B',
      blockers: [],
      completedActionIds,
      coveredActionIds: completedActionIds,
      requiresEfficacy: true,
      requiresClosureReason: false,
      reason: null,
    };
  }

  if (!input.closureReason?.trim()) {
    return {
      canClose: false,
      case: 'C',
      blockers: ['closure_reason_missing'],
      completedActionIds,
      coveredActionIds,
      requiresEfficacy: false,
      requiresClosureReason: true,
      reason: 'É necessário informar o motivo do encerramento.',
    };
  }

  return {
    canClose: true,
    case: 'C',
    blockers: [],
    completedActionIds,
    coveredActionIds,
    requiresEfficacy: false,
    requiresClosureReason: true,
    reason: null,
  };
}

export function canCloseIssue(input: {
  actions: CloseSubjectAction[];
  attempts: EfficacyAttempt[];
  closureReason?: string | null;
  reopenAt?: string | null;
  metadata?: IssueMetadata | null;
}): CloseBlock {
  const decision = evaluateIssueClosure(input);
  if (!decision.canClose) {
    return { allowed: false, reason: decision.reason ?? 'O problema não pode ser encerrado.' };
  }
  return { allowed: true };
}

export function closurePromptText(opportunityCount: number): string {
  const base = 'Motivo do encerramento. Sem contramedida concluída, o motivo é obrigatório. Com contramedida concluída, o encerramento só vale depois da eficácia confirmada.';
  if (opportunityCount > 0) {
    return `${base}\n\nEste problema possui oportunidade(s) de melhoria vinculada(s).`;
  }
  return base;
}

export function latestEfficacyAttempt(attempts: EfficacyAttempt[]): EfficacyAttempt | null {
  if (attempts.length === 0) return null;
  return [...attempts].sort((left, right) => new Date(right.at).getTime() - new Date(left.at).getTime())[0];
}

export function canConfirmEfficacy(input: {
  severity: string;
  verifierId?: string | null;
  problemOwnerId?: string | null;
  actorId?: string | null;
  actorRole?: string | null;
}): CloseBlock {
  const managerial = MANAGERIAL_ROLES.has((input.actorRole ?? '').toLowerCase());
  const verifier = input.verifierId?.trim() || null;
  if (input.severity === 'critical' && !verifier) {
    return { allowed: false, reason: 'Problema crítico exige um responsável pela verificação.' };
  }
  if (!input.actorId) {
    return { allowed: false, reason: 'A confirmação precisa de um usuário identificado.' };
  }
  if (verifier) {
    if (input.actorId === verifier || managerial) return { allowed: true };
    return { allowed: false, reason: 'Somente o responsável pela verificação ou um perfil gerencial pode confirmar.' };
  }
  if (input.problemOwnerId && input.actorId === input.problemOwnerId) return { allowed: true };
  if (managerial) return { allowed: true };
  return { allowed: false, reason: 'Sem verificador definido, a confirmação fica com o responsável pelo problema ou com um perfil gerencial.' };
}

export function verificationDueAt(input: {
  validationMode?: string | null;
  windowDays?: number | null;
  explicitDueAt?: string | null;
  actions: CloseSubjectAction[];
}): string | null {
  if (input.validationMode === 'time' && input.windowDays != null && input.windowDays > 0) {
    const stamps = input.actions
      .filter(isRelevantCountermeasure)
      .map((action) => action.completed_at)
      .filter((value): value is string => Boolean(value))
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value));
    if (stamps.length === 0) return null;
    const start = Math.max(...stamps);
    return new Date(start + input.windowDays * 24 * 60 * 60 * 1000).toISOString();
  }
  if (input.explicitDueAt && Number.isFinite(new Date(input.explicitDueAt).getTime())) {
    return input.explicitDueAt;
  }
  return null;
}

export type GovernancePresentation =
  | 'acoes_pendentes'
  | 'contramedidas_executadas'
  | 'aguardando_verificacao'
  | 'verificacao_vencida'
  | 'nao_eficaz'
  | 'eficacia_confirmada'
  | 'encerrado'
  | 'sem_contramedida';

export const GOVERNANCE_LABELS: Record<GovernancePresentation, string> = {
  acoes_pendentes: 'Ações pendentes',
  contramedidas_executadas: 'Contramedidas executadas',
  aguardando_verificacao: 'Aguardando verificação',
  verificacao_vencida: 'Verificação vencida',
  nao_eficaz: 'Não eficaz — nova contramedida necessária',
  eficacia_confirmada: 'Eficácia confirmada',
  encerrado: 'Encerrado',
  sem_contramedida: 'Sem contramedida',
};

export function governanceState(input: {
  status: string;
  actions: CloseSubjectAction[];
  attempts: EfficacyAttempt[];
  dueAt?: string | null;
  now?: string;
}): GovernancePresentation {
  if (input.status === 'closed') return 'encerrado';
  const active = openCountermeasures(input.actions);
  const latest = [...input.attempts].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())[0];
  if (active.length > 0) {
    return latest?.result === 'ineffective' ? 'nao_eficaz' : 'acoes_pendentes';
  }
  const relevant = input.actions.filter(isRelevantCountermeasure);
  if (relevant.length === 0) return 'sem_contramedida';
  if (input.attempts.some((attempt) => attemptCovers(attempt, relevant))) return 'eficacia_confirmada';
  const due = input.dueAt ? new Date(input.dueAt).getTime() : null;
  const now = new Date(input.now ?? new Date().toISOString()).getTime();
  if (due != null && Number.isFinite(due) && now > due) return 'verificacao_vencida';
  if (input.attempts.length === 0) return 'contramedidas_executadas';
  return 'aguardando_verificacao';
}

export function statusAfterIneffectiveCheck(current: IssueStatus): IssueStatus {
  if (current === 'validation' || current === 'monitoring') return 'execution';
  return current;
}

export function previousCountermeasureStaysCompleted(
  actions: CountermeasureRef[],
  actionId: string,
): boolean {
  return actions.some((action) => action.id === actionId && action.status === 'completed');
}

export function nextCountermeasureKeepsProblem(problemId: string): { problem_id: string } {
  return { problem_id: problemId };
}

export interface OperationalSupport {
  origin: OperationalOrigin | null;
  openingWip: number | null;
  currentWip: number | null;
  confirmsEfficacy: false;
}

/** Snapshot da origem, quando existir. Sem leitura confiável do agora, não compara. */
export function operationalSupport(metadata: unknown, currentWip?: number | null): OperationalSupport {
  const origin = readOperationalOrigin(metadata);
  const openingWip = origin?.wip ?? null;
  const current = currentWip == null || !Number.isFinite(currentWip) ? null : currentWip;
  return {
    origin,
    openingWip,
    currentWip: current,
    confirmsEfficacy: false,
  };
}
